import { Star } from 'lucide-react';
import { useState } from 'react';

import { cn } from '@/lib/utils';

import config from '../config/masterclass-rating-config.json';
import { StarRatingProps } from '../types';

export const StarRating = ({ value, onChange, label }: StarRatingProps) => {
  const [hoverValue, setHoverValue] = useState<number | null>(null);
  const [justClicked, setJustClicked] = useState<number | null>(null);

  const displayValue = hoverValue ?? value ?? 0;
  const { minRating, maxRating } = config.validation;

  const handleClick = (star: number) => {
    onChange(star);
    setJustClicked(star);

    setTimeout(() => setJustClicked(null), config.animations.starClick.duration);
  };

  return (
    <div className="space-y-2">
      {label && <p className="text-center font-body text-base text-livelinx-text">{label}</p>}
      <div
        className="flex justify-center gap-2"
        role="radiogroup"
        aria-label={label || 'Star rating'}
      >
        {Array.from({ length: maxRating - minRating + 1 }, (_, i) => i + minRating).map((star) => (
          <button
            key={star}
            type="button"
            role="radio"
            aria-checked={value === star}
            aria-label={`${star} star${star > 1 ? 's' : ''}`}
            onClick={() => handleClick(star)}
            onMouseEnter={() => setHoverValue(star)}
            onMouseLeave={() => setHoverValue(null)}
            style={{
              transitionDelay: `${star * 50}ms`,
            }}
            className={cn(
              'transition-all duration-200 ease-out',
              'hover:-rotate-12 hover:scale-125',
              'rounded focus:outline-none',
              'active:scale-90',
              justClicked === star && 'animate-[bounce_0.6s_ease-in-out]',
              star <= displayValue && 'animate-[scale-in_0.3s_ease-out]'
            )}
          >
            <Star
              className={cn(
                'h-8 w-8 transition-all duration-300',
                star <= displayValue && 'animate-glow'
              )}
              style={{
                fill: star <= displayValue ? config.styles.starColor : 'none',
                color:
                  star <= displayValue ? config.styles.starColor : config.styles.starEmptyColor,
                filter:
                  star <= displayValue ? 'drop-shadow(0 0 8px rgba(252, 189, 83, 0.8))' : 'none',
              }}
            />
          </button>
        ))}
      </div>
    </div>
  );
};
